import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useAuth, pickPostLoginPath } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import PasswordInput from "@/components/PasswordInput";
import PublicPageShell from "@/components/PublicPageShell";
import FieldError from "@/components/a11y/FieldError";
import PoliteStatus from "@/components/a11y/PoliteStatus";
import LoginWallContestContext from "@/components/LoginWallContestContext";
import { useConfig } from "@/context/ConfigContext";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { fetchTimezones, detectBrowserTimezone } from "@/lib/timezones";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Backlog B.2.x — safe redirect-target validator. Mirrors
 * `safeNext` in `Login.jsx` (byte-identical semantics) so that
 * a `?next=` value which round-trips through signup lands on
 * exactly the same subset of allowed targets as one that lands
 * on login directly. Duplicated deliberately — a lift-into-a-
 * shared-lib refactor is a separate cleanup pass, out of scope
 * for the Feb 2026 contest-context dispatch.
 */
function safeNext(raw) {
  if (!raw || typeof raw !== "string") return null;
  if (!raw.startsWith("/")) return null;
  if (raw.startsWith("//")) return null;
  if (raw.includes(":")) return null;
  if (raw.length > 512) return null;
  return raw;
}

export default function Signup() {
  const { setSession, refreshMe } = useAuth();
  const { providerDisplayLabel: providerLabel } = useConfig();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextParam = safeNext(params.get("next"));
  // Dispatch A (Feb 2026) — contest-context framing when the user
  // arrived here from `/contest/enter`. Signup CTA is already the
  // primary action on this page, so the only UX shift is the copy
  // block above the panel + the bottom "Log in" link preserving
  // `next` so the round-trip back to login also carries the
  // contest context.
  const isContestEntryNext = nextParam === "/contest/enter";
  const loginHref = isContestEntryNext
    ? "/login?next=%2Fcontest%2Fenter"
    : "/login";

  // Partner-first launch posture: bare `/signup` (no `?role=`) defaults
  // to the provider role so recruiting traffic lands on the partner
  // signup pane without a click. `?role=customer` explicitly opts
  // into the dog-owner path. `?role=provider` still resolves to
  // provider (unchanged from before this flip). Every marketing CTA
  // that currently uses `?role=provider` continues to work byte-
  // identically; every CTA that uses `?role=customer` continues to
  // work byte-identically; the change is only for the un-parametrized
  // `/signup` landing case.
  const initialRole = params.get("role") === "customer" ? "customer" : "provider";
  const [role, setRole] = useState(initialRole);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // Track B — required IANA timezone on signup. Pre-filled from
  // `Intl.DateTimeFormat` on mount; user can override before submit.
  // Empty when Intl is unavailable / stubbed — submit stays disabled
  // until the user picks one from the fetched list.
  const [tz, setTz] = useState(() => detectBrowserTimezone());
  const [tzList, setTzList] = useState([]);
  // Phase L.2 P1 — persistent inline error surface (audit SU-2).
  // Toast is retained for the visual "just-happened" cue, but a
  // stable `<FieldError role="alert">` region gives SR users a
  // non-ephemeral error they can revisit.
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchTimezones().then((options) => {
      if (alive) setTzList(options);
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const r = params.get("role");
    if (r === "customer" || r === "provider") setRole(r);
  }, [params]);

  const redirectByRole = (r) =>
    navigate(r === "provider" ? "/dashboard/provider" : "/dashboard/customer", {
      replace: true,
    });

  // Post-signup redirect: signup always creates exactly one capability,
  // so `redirectByRole` is equivalent to `pickPostLoginPath` here. Kept
  // as a separate helper for clarity at the call sites.
  //
  // Dispatch A (Feb 2026) — if a same-origin `?next=` value was
  // preserved through the flow (e.g. `/contest/enter` for contest
  // entrants), prefer it. Matches the Login-side behavior so a
  // signup flow triggered from ProtectedRoute lands on the caller's
  // original destination instead of the default dashboard.
  const redirectByCapability = (u) => {
    if (nextParam) {
      navigate(nextParam, { replace: true });
      return;
    }
    navigate(pickPostLoginPath(u) || "/", { replace: true });
  };

  const validate = () => {
    if (!name.trim()) return "Please enter your name.";
    if (!EMAIL_RE.test(email)) return "Please enter a valid email address.";
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (!tz) return "Please choose your timezone.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setFormError(err);
      toast.error(err);
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      // P1 (2026-07-24) — signup is enumeration-safe: returns 200
      // `{email, message}` on BOTH the fresh-signup branch and the
      // silent-dedupe branch. No JWT is issued from this endpoint.
      // The user completes the flow by signing in with the password
      // they just supplied — legitimate case: succeeds; already-
      // registered case: still needs the CORRECT password to sign
      // in. Attacker cannot enumerate via the signup response alone
      // (both branches are byte-identical for the same input email).
      await api.post("/auth/signup", {
        email,
        password,
        name,
        role,
        timezone: tz,
      });
      // Attempt an immediate sign-in with the same credentials for a
      // smooth UX on the fresh-signup branch. On the silent-dedupe
      // branch with a wrong password, this login will fail with a
      // generic 401 (indistinguishable from any wrong-password
      // attempt on any account — the login endpoint is enumeration-
      // safe too, P1).
      try {
        const { data: loginData } = await api.post("/auth/login", {
          email, password,
        });
        setSession(loginData.access_token, loginData.user);
        await refreshMe();
        toast.success(`Welcome to PooPluckers, ${loginData.user.name}!`);
        // Commit B — §4.1 funnel step. Only when the user arrived
        // from /contest/enter. Non-contest signups don't fire this.
        if (isContestEntryNext) {
          trackEvent("contest.signup_complete");
        }
        redirectByCapability(loginData.user);
      } catch (loginErr) {
        // Sign-in failed after signup. Show a neutral message that
        // doesn't reveal whether the email was fresh or duplicate.
        toast.success("Account setup received. Please sign in to continue.");
        window.location.assign(
          `/login?email=${encodeURIComponent(email)}`,
        );
      }
    } catch (e2) {
      const msg =
        formatApiErrorDetail(e2.response?.data?.detail) || e2.message;
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    // Phase 4b Step 1 — kick off Google OAuth Authorization Code flow.
    // The `role` chosen in the signup form is passed as a state-hint so
    // the callback handler can create the right profile shell on first
    // signup. See Login.jsx for the longer comment.
    setGoogleLoading(true);
    try {
      const { data } = await api.get("/auth/google/redirect-url", {
        params: { role_hint: role },
      });
      if (!data?.state || !data?.redirect_url) {
        throw new Error("Missing state or redirect_url from /auth/google/redirect-url");
      }
      sessionStorage.setItem("pp_google_oauth_state", data.state);
      sessionStorage.setItem("pp_google_oauth_role_hint", role);
      window.location.assign(data.redirect_url);
    } catch (e2) {
      const detail = e2.response?.data;
      if (e2.response?.status === 503 && detail?.google_code === "creds_missing") {
        toast.error("Google sign-in is not configured yet. Please use email + password.");
      } else {
        toast.error(formatApiErrorDetail(detail?.detail) || e2.message);
      }
      setGoogleLoading(false);
    }
  };

  return (
    <PublicPageShell mainClass="flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <Link to="/" data-testid="signup-logo-link" className="block">
            <Logo variant="full" className="h-32 w-32" />
          </Link>
          <h1
            className="mt-2 font-heading text-3xl font-bold text-pp-text"
            data-testid="signup-hero-heading"
          >
            {isContestEntryNext ? "One quick account" : "Create your account"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isContestEntryNext
              ? "Sign up to enter your pup in the Cutest Pluck contest."
              : "Spotless yards in just a sec."}
          </p>
        </div>

        {isContestEntryNext && <LoginWallContestContext />}

        <div className="rounded-3xl border border-border bg-pp-surface p-8 shadow-sm">
          {/* Phase L.2 P3 — audit SU-3. sr-only intermediate H2 so
              the H1 (hero above) → H3 (SharedFooter columns) heading
              hierarchy doesn't skip a level for screen-reader users
              navigating by heading. */}
          <h2 className="sr-only" data-testid="signup-form-h2">
            Create your PooPluckers account
          </h2>
          {/* MOCKED: Google OAuth — Phase 1 stub */}
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogle}
            disabled={googleLoading}
            className="h-12 w-full rounded-full border-border bg-pp-surface text-pp-text hover:bg-secondary"
            data-testid="signup-google-button"
          >
            {googleLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.85 0-5.27-1.92-6.13-4.51H2.18v2.84A11 11 0 0 0 12 23z" />
                <path fill="#FBBC05" d="M5.87 14.14a6.6 6.6 0 0 1 0-4.28V7.02H2.18a11 11 0 0 0 0 9.96l3.69-2.84z" />
                <path fill="#EA4335" d="M12 5.25c1.62 0 3.06.56 4.21 1.65l3.15-3.15C17.45 1.98 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.02l3.69 2.84C6.73 7.17 9.15 5.25 12 5.25z" />
              </svg>
            )}
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {/* Role toggle — segmented control.
              Phase L.2 P1 (audit SU-1): aligned to the Pricing
              `AudienceToggle` a11y pattern — `role="group"` +
              `aria-label` on the wrapper + `aria-pressed` per
              button. Enforced by
              `components/a11y/segmentedToggle.contract.test.jsx`. */}
          <div
            className="mb-6 grid grid-cols-2 gap-2 rounded-full bg-secondary p-1"
            role="group"
            aria-label="Account type"
            data-testid="role-toggle"
          >
            {[
              { v: "provider", label: `I'm a ${providerLabel}` },
              { v: "customer", label: "I'm a dog owner" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setRole(opt.v)}
                aria-pressed={role === opt.v}
                className={cn(
                  "rounded-full px-4 py-2 text-sm font-semibold transition-all",
                  role === opt.v
                    ? "bg-brand-orange text-white shadow-sm"
                    : "text-pp-text hover:text-brand-orange"
                )}
                data-testid={`role-toggle-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
            {/* Phase L.2 P1 — persistent form-level error announcement
                (audit SU-2). Non-ephemeral SR alert; toast fires in
                parallel for the visual cue. */}
            <FieldError
              id="signup-form-error"
              message={formError}
              testId="signup-form-error"
              className="mt-0"
            />
            <div>
              <Label htmlFor="name" className="text-sm font-semibold text-pp-text">
                Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="mt-2 h-12 rounded-full border-border bg-pp-surface px-5"
                required
                data-testid="signup-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm font-semibold text-pp-text">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-2 h-12 rounded-full border-border bg-pp-surface px-5"
                required
                data-testid="signup-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-semibold text-pp-text">
                Password
              </Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                className="mt-2"
                required
                data-testid="signup-password-input"
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                Minimum 8 characters.
              </p>
            </div>
            <div>
              <Label htmlFor="signup-tz-select" className="text-sm font-semibold text-pp-text">
                Timezone
              </Label>
              <select
                id="signup-tz-select"
                data-testid="signup-tz-select"
                value={tz}
                onChange={(e) => setTz(e.target.value)}
                required
                className="pp-select mt-2 h-12 w-full rounded-full border px-5 text-sm"
              >
                {!tz && <option value="">— pick a timezone —</option>}
                {tzList.map((z) => (
                  <option key={z} value={z}>{z}</option>
                ))}
              </select>
              <p className="mt-1.5 text-xs text-muted-foreground">
                Auto-detected from your browser. Change if needed.
              </p>
            </div>

            <Button
              type="submit"
              disabled={submitting || !tz}
              className="h-12 w-full rounded-full bg-pp-purple text-base text-white hover:bg-pp-purple-dark"
              data-testid="signup-submit-button"
              aria-busy={submitting || undefined}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  Creating account…
                </>
              ) : (
                `Create ${role === "provider" ? providerLabel : "customer"} account`
              )}
            </Button>
            {/* Phase L.2 P2 — audit SU-4. Polite live region so SR
                gets the submit-in-progress + settled announcement.
                Region stays mounted (empty when idle). */}
            <PoliteStatus
              visuallyHidden
              message={submitting ? "Creating your account…" : ""}
              testId="signup-submit-status"
            />
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link
              to={loginHref}
              className="font-semibold text-brand-orange hover:underline"
              data-testid="signup-login-link"
            >
              Log in
            </Link>
          </p>
        </div>
      </div>
    </PublicPageShell>
  );
}
